import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faEye, 
  faCalendar,
  faShoppingCart,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { ToastContainer } from 'react-toastify';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
   const [forceUpdateKey, setForceUpdateKey] = useState(0);
    const [cartCount, setCartCount] = useState(0);
   const [hasIncompatibleItems, setHasIncompatibleItems] = useState(false);

  const [activeTab, setActiveTab] = useState('content');

  useEffect(() => {
    fetchBlogDetail();
    loadCartItems();
  }, [id]);

  const loadCartItems = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartItems(storedCartItems);
    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
  };

  const fetchBlogDetail = async () => {
    try {
      const response = await axios.get(`https://afric-fresh-backend.onrender.com/blogs/main/${id}`);
      setBlog(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog detail:', error);
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    const existingItem = cartItems.find(item => item._id === product._id);
    let updatedCart;

    if (existingItem) {
      updatedCart = cartItems.map(item =>
        item._id === product._id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      updatedCart = [...cartItems, { ...product, quantity: 1 }];
    }

    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    setCartItems(updatedCart);
    setCartCount(updatedCart.reduce((acc, item) => acc + item.quantity, 0));
    
    toast.success(`${product.name} added to cart!`, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: true,
    });
  };

  const updateCartCount = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
    
    const hasIncompatible = storedCartItems.some(item => 
      ["Large Box", "Small Box", "Discount"].includes(item.orderType)
    );
    setHasIncompatibleItems(hasIncompatible);
  };

  const forceUpdateProducts = () => {
    setForceUpdateKey(prevKey => prevKey + 1);
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item._id === productId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#007D44]"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-gray-600">Blog not found</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pt-16">
      <Navbar cartCount={cartCount} updateCartCount={updateCartCount} forceUpdateProducts={forceUpdateProducts} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-[400px] object-cover"
          />
        </div>
  
        {/* Blog Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <span className="bg-[#007D44] text-white px-4 py-1 rounded-full text-sm">
              {blog.category}
            </span>
            {blog.tags.map(tag => (
              <span key={tag} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                {tag}
              </span>
            ))}
          </div>
  
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {blog.title}
          </h1>
  
          <div className="flex items-center space-x-6 text-gray-500">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faCalendar} />
              <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faClock} />
              <span>{blog.readTime} min read</span>
            </div>
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faEye} />
              <span>{blog.views} views</span>
            </div>
          </div>
        </div>
  
        {/* Tab Navigation */}
        <div className="mb-8 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('content')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'content'
                  ? 'border-[#007D44] text-[#007D44]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Article
            </button>
            {blog.products && blog.products.length > 0 && (
              <button
                onClick={() => setActiveTab('products')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'products'
                    ? 'border-[#007D44] text-[#007D44]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Ingredients ({blog.products.length})
              </button>
            )}
          </nav>
        </div>
  
        {/* Tab Content */}
        {activeTab === 'content' ? (
          <div className="prose prose-lg max-w-none">
            <div dangerouslySetInnerHTML={{ __html: blog.content }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blog.products.map(product => (
              <div 
                key={product._id} 
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  {isInCart(product._id) && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
                      <FontAwesomeIcon icon={faCheck} size="xs" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {product.name}
                  </h3>
                  <p className="text-[#007D44] font-bold mb-3">${product.price}</p>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className={`w-full flex justify-center items-center px-3 py-2 rounded-lg ${
                      isInCart(product._id)
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#007D44] text-white hover:bg-[#006435]'
                    }`}
                  >
                    <FontAwesomeIcon 
                      icon={isInCart(product._id) ? faCheck : faShoppingCart} 
                      className="mr-2" 
                    />
                    {isInCart(product._id) ? 'Added to Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
      <ToastContainer />
    </div>
  )
};

export default BlogDetail;